import { z } from 'zod'

import type { CardfileData } from '@/api/cardfile/cardfileApi'
import { getLegalFieldsFromCardfile } from '@/components/Cardfile/lib/cardfileToLegalFields'
import type { StoreCreationFormType } from '@/components/EntityCreationModals/StoreCreationModal/StoreCreationModal.config'
import { isStoreUnassigned } from '@/components/EntityCreationModals/StoreCreationModal/storeFormHelpers'
import { supplierFormSchema } from '@/pages/Exchanges/tabs/StockImports/lib/stockImportsValidation'
import type {
  ProductSupplierDto,
  ProductSupplierRequest,
  StoreModel,
} from '@/pages/Exchanges/tabs/StockImports/types/stockImports.types'
import type { OptionType } from '@/types'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

export const NEW_STORE_ID_PREFIX = 'new-store-'
const SUPPLIER_PHONE_MAX_DIGITS = 19
const VAT_NUMBER_LENGTH = 9

export const VAT_NUMBER_MASK = Array.from(
  { length: VAT_NUMBER_LENGTH },
  () => /\d/
)

export const SUPPLIER_PHONE_MASK = (value: string) => {
  const digits = value
    .replace(/\D/g, '')
    .slice(0, SUPPLIER_PHONE_MAX_DIGITS)
    .split('')
    .map(() => /\d/)
  return value.trim().startsWith('+') ? ['+', ...digits] : digits
}

const optionSchema = z.object({ value: z.string(), label: z.string() })

export const supplierCreationSchema = supplierFormSchema.extend({
  stores: z.array(optionSchema),
})

export type SupplierCreationFormType = z.infer<typeof supplierCreationSchema>

export type PendingStore = {
  id: string
  values: StoreCreationFormType
}

const toStoreOption = (store: StoreModel): OptionType => ({
  value: store.id,
  label: store.description,
})

const isStoreOfSupplier = (store: StoreModel, supplierId: string | null) =>
  supplierId !== null && store.productSupplierShortDto?.id === supplierId

export const buildSupplierFormDefaults = (
  supplier: ProductSupplierDto | null,
  stores: StoreModel[]
): SupplierCreationFormType => ({
  description: supplier?.description ?? '',
  vatNumber: supplier?.vatNumber ?? '',
  fullLegalName: supplier?.fullLegalName ?? '',
  shortLegalName: supplier?.shortLegalName ?? '',
  legalAddress: supplier?.legalAddress ?? '',
  postalAddress: supplier?.postalAddress ?? '',
  phoneNumber: supplier?.phoneNumber ?? '',
  email: supplier?.email ?? '',
  stores: stores
    .filter((store) => isStoreOfSupplier(store, supplier?.id ?? null))
    .map(toStoreOption),
})

export const buildSupplierStoreOptions = (
  supplierId: string | null,
  stores: StoreModel[],
  pendingStores: PendingStore[]
): OptionType[] => [
  ...stores
    .filter(
      (store) =>
        isStoreUnassigned(store) || isStoreOfSupplier(store, supplierId)
    )
    .map(toStoreOption),
  ...pendingStores.map(toPendingStoreOption),
]

export const toPendingStoreOption = (store: PendingStore): OptionType => ({
  value: store.id,
  label: `${store.values.description} (новый)`,
})

export const getStoreBindingChanges = (
  initialStoreIds: string[],
  selectedStoreIds: string[]
) => {
  const isNew = (id: string) => id.startsWith(NEW_STORE_ID_PREFIX)
  return {
    attachIds: selectedStoreIds.filter(
      (id) => !isNew(id) && !initialStoreIds.includes(id)
    ),
    detachIds: initialStoreIds.filter((id) => !selectedStoreIds.includes(id)),
    newStoreIds: selectedStoreIds.filter(isNew),
  }
}

export const toProductSupplierRequest = (
  values: SupplierCreationFormType
): ProductSupplierRequest => ({
  description: values.description,
  vatNumber: values.vatNumber,
  fullLegalName: values.fullLegalName,
  shortLegalName: values.shortLegalName,
  legalAddress: values.legalAddress,
  postalAddress: values.postalAddress,
  phoneNumber: denormalizePhoneForBackend(values.phoneNumber),
  email: values.email,
})

type SupplierCardfileField =
  | 'description'
  | 'fullLegalName'
  | 'shortLegalName'
  | 'legalAddress'
  | 'postalAddress'

export const getSupplierFieldsFromCardfile = (
  data: CardfileData,
  current: Pick<SupplierCreationFormType, 'description' | 'postalAddress'>
): Array<[SupplierCardfileField, string]> => {
  const legalFields = getLegalFieldsFromCardfile(data).flatMap(
    ([name, value]): Array<[SupplierCardfileField, string]> =>
      name === 'ipRegistrationDate' ? [] : [[name, value]]
  )
  const shortName = legalFields.find(([name]) => name === 'shortLegalName')
  const address = legalFields.find(([name]) => name === 'legalAddress')
  const fallbacks: Array<[SupplierCardfileField, string]> = []
  if (shortName && !current.description.trim())
    fallbacks.push(['description', shortName[1]])
  if (address && !current.postalAddress.trim())
    fallbacks.push(['postalAddress', address[1]])
  return [...legalFields, ...fallbacks]
}
